"use client";

import * as React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { EmptyState } from "@/components/ui/primitives/data-display";
import type { EmptyStateVariant } from "@/components/ui/primitives/empty-state";
import {
  DateRangeDropdown,
  type DateRangeValue,
} from "@/components/ui/primitives/date-range-filter";
import { Button } from "@/components/ui/primitives/button";
import {
  ColumnSortControls,
  ColumnSortIndicator,
} from "@/components/ui/primitives/column-sort-controls";
import { DeleteButton } from "@/components/ui/primitives/delete-button";
import {
  InlineFilters,
  type InlineFilterConfig,
} from "@/components/ui/primitives/inline-filters";
import { ModernPagination } from "@/components/ui/primitives/modern-pagination";
import {
  SearchableSelect,
  type SearchableSelectOption,
} from "@/components/ui/primitives/searchable-select";
import { ProcedureFilterSelect } from "@/components/ui/primitives/procedure-filter-select";
import { SpecificationFilterSelect } from "@/components/ui/primitives/specification-filter-select";
import { TableCheckbox } from "@/components/ui/primitives/table-checkbox";
import {
  EnhancedTableToolbar,
  type AvailableFilter,
} from "@/components/ui/primitives/table-toolbar";
import type { TableColumn } from "@/components/ui/primitives/table-settings-menu";
import {
  useFilterVisibility,
  type FilterVisibilityPersistence,
} from "@/lib/hooks/tables/useFilterVisibility";
import {
  applyPageSelectAll,
  isBulkSelectionAtCap,
} from "@/lib/hooks/tables/row-selection-limits";
import {
  applyColumnSortDirection,
  applyMovableOrder,
  isColumnOrderStateControlled,
  toSelectorColumn,
} from "@/lib/utils/column-order";
import {
  coerceSingleSelectFilters,
  normalizeDateRangeValue,
} from "@/lib/utils/filter-utils";
import { appliedFilterIdsOf } from "@/lib/utils/filter-visibility";
import { cn } from "@/lib/utils/general-utils";
import {
  cellContentNeedsMinWidthZero,
  getCellTextClass,
  getColumnSizeStyle,
} from "@/lib/utils/table-styles";
import type { ColumnMetaConfig } from "@/types/central/ui";
import {
  flexRender,
  type Column,
  type ReactTable as TanStackTable,
  type RowData,
  type StockFeatures,
} from "@tanstack/react-table";

const { useCallback, useEffect, useMemo } = React;

type FilterValues = Record<
  string,
  string | string[] | { from: string; to: string }
>;

const DEFAULT_PAGE_SIZE_OPTIONS: number[] = [10, 25, 50, 100];

/** Lets the table scroller shrink inside a flex column. */
export const TABLE_SCROLL_SHRINK_CLASS = "min-h-0 shrink";

/**
 * Shrink the overflow wrapper inside a flex column. Height is content-sized
 * unless `fillHeight` is set (embedded/split views).
 */
export const TABLE_VIEWPORT_SCROLL_CLASS = TABLE_SCROLL_SHRINK_CLASS;

const TABLE_FILL_HEIGHT_CLASS = "h-full min-h-0 flex-1";

// =============================================================================
// SORTABLE HEADER
// =============================================================================

export function SortableHeader<TData extends RowData>({
  column,
  label,
}: {
  readonly column: Column<StockFeatures, TData, unknown>;
  readonly label: string;
}) {
  const sorted = column.getIsSorted();

  return (
    <button
      type="button"
      className="inline-flex items-center gap-1 select-none hover:text-gray-700 transition-colors -ml-0.5 px-0.5 py-0.5 rounded"
      onClick={() => column.toggleSorting()}
    >
      <span>{label}</span>
      <ColumnSortIndicator sortDirection={sorted} />
    </button>
  );
}

type ToolbarSortColumn = {
  getCanSort?: () => boolean;
  getIsSorted?: () => false | "asc" | "desc";
  toggleSorting?: (desc?: boolean, isMulti?: boolean) => void;
  clearSorting?: () => void;
};

function filterSortControlsForColumn(column: ToolbarSortColumn | undefined) {
  if (!column?.getCanSort?.()) return null;

  const sortDirection = column.getIsSorted?.() ?? false;

  return (
    <ColumnSortControls
      sortDirection={sortDirection}
      onSortAsc={() => column.toggleSorting?.(false)}
      onSortDesc={() => column.toggleSorting?.(true)}
      onClearSort={() => column.clearSorting?.()}
    />
  );
}

// =============================================================================
// BULK ACTION BAR
// =============================================================================

export interface BulkAction {
  id: string;
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  variant?: "default" | "danger";
  isLoading?: boolean;
  disabled?: boolean;
  title?: string;
}

function BulkActionBar({
  selectedCount,
  onClear,
  actions,
}: {
  readonly selectedCount: number;
  readonly onClear: () => void;
  readonly actions: BulkAction[];
}) {
  if (selectedCount === 0) return null;

  return (
    <div className="mb-2 flex min-h-10 shrink-0 items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm shadow-sm">
      <span className="shrink-0 self-center font-semibold leading-none text-gray-800">
        {selectedCount} selected
      </span>
      <div className="h-4 w-px shrink-0 self-center bg-gray-300" />
      <div className="ml-auto flex items-center gap-2">
        {actions.map((action) =>
          action.variant === "danger" ? (
            <DeleteButton
              key={action.id}
              onClick={action.onClick}
              isLoading={action.isLoading}
              disabled={action.disabled}
              size="sm"
              className="h-8"
              title={action.title}
            >
              {action.label}
            </DeleteButton>
          ) : (
            <Button
              key={action.id}
              type="button"
              variant="outline"
              size="sm"
              disabled={action.disabled || action.isLoading}
              title={action.title}
              onClick={(e) => {
                e.stopPropagation();
                action.onClick();
              }}
              className="h-8 gap-1.5"
              icon={action.icon}
            >
              {action.label}
            </Button>
          ),
        )}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onClear}
          aria-label="Clear selection"
          className="h-8"
        >
          Clear
        </Button>
      </div>
    </div>
  );
}

// =============================================================================
// DATA TABLE PROPS
// =============================================================================

export type TableDensity = "comfortable" | "compact";

export interface DataTableProps<TData extends RowData> {
  table: TanStackTable<StockFeatures, TData>;
  isLoading?: boolean;
  isFetching?: boolean;
  error?: Error | null;
  totalCount?: number;

  onPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  pageSizeOptions?: number[];

  onRowClick?: (row: TData) => void;
  rowClassName?: (row: TData) => string;
  /** Fill a bounded parent (split views). Full-page lists should omit this so
   * 50/100 rows per page can grow and the page scrolls. */
  fillHeight?: boolean;

  enableRowSelection?: boolean;
  onRowSelectionChange?: (selectedRows: TData[]) => void;
  bulkActions?: BulkAction[];

  emptyMessage?: string;
  /** Replaces the empty-state title. Omitted uses the variant title. */
  emptyTitle?: string;
  /** Optional control under the empty-state description, such as Clear search. */
  emptyAction?: React.ReactNode;
  /** EmptyState variant for Lottie-based empty state */
  emptyVariant?: EmptyStateVariant;
  className?: string;

  /** Row density. "compact" reduces row height for denser displays. */
  density?: TableDensity;
  /** Make the <thead> sticky within the scroll container. */
  stickyHeader?: boolean;
  /** Freeze the first data column so it stays visible during horizontal scroll. */
  stickyFirstColumn?: boolean;
  /** Hide bottom pagination controls (e.g. infinite scroll sections). */
  hidePagination?: boolean;
  /** Wrapper only scrolls horizontally — use inside a vertical scroll container. */
  horizontalScrollOnly?: boolean;
  /** Fixed-height scroll container for the table body; pagination stays below. */
  scrollContainerClassName?: string;
}

export function getStickyFirstColumnClassName(
  stickyFirstColumn: boolean,
): string {
  if (!stickyFirstColumn) return "";
  // Opaque fills only — row hover/selection uses /50 tints, and inherit would
  // let scrolled cells show through the frozen column.
  return "sticky left-0 z-10 bg-white group-hover:bg-gray-50 group-data-[state=selected]:bg-blue-50 group-hover:group-data-[state=selected]:bg-blue-50";
}

export function getStickyFirstColumnHeaderClassName(
  stickyFirstColumn: boolean,
): string {
  if (!stickyFirstColumn) return "";
  return "sticky left-0 z-20 bg-white";
}

// =============================================================================
// DATA TABLE
// =============================================================================

export function DataTable<TData extends RowData>({
  table,
  isLoading = false,
  isFetching = false,
  error = null,
  totalCount = 0,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  onRowClick,
  rowClassName,
  fillHeight = false,
  enableRowSelection = false,
  onRowSelectionChange,
  bulkActions = [],
  emptyMessage = "No data available",
  emptyTitle,
  emptyAction,
  emptyVariant,
  className,
  density = "compact",
  stickyHeader = false,
  stickyFirstColumn = false,
  hidePagination = false,
  horizontalScrollOnly = false,
  scrollContainerClassName,
}: Readonly<DataTableProps<TData>>) {
  const rows = table.getRowModel().rows;
  const columns = table.getVisibleLeafColumns();

  const showSkeleton = isLoading || (isFetching && rows.length === 0);
  const showEmpty = !isLoading && !isFetching && !error && rows.length === 0;

  // Row selection tracking
  const rowSelectionState = table.state.rowSelection;
  const selectedRows = useMemo(() => {
    if (!enableRowSelection) return [];
    return table.getSelectedRowModel().rows.map((r) => r.original);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enableRowSelection, rowSelectionState]);

  useEffect(() => {
    if (enableRowSelection && onRowSelectionChange) {
      onRowSelectionChange(selectedRows);
    }
  }, [enableRowSelection, onRowSelectionChange, selectedRows]);

  // Pagination
  const paginationState = table.state.pagination;
  const pageCount = table.getPageCount();

  const handlePageChange = useCallback(
    (page: number) => {
      if (enableRowSelection) {
        table.resetRowSelection();
      }
      if (onPageChange) {
        onPageChange(page);
      } else {
        table.setPageIndex(page - 1);
      }
    },
    [table, onPageChange, enableRowSelection],
  );

  const handlePageSizeChange = useCallback(
    (size: number) => {
      if (enableRowSelection) {
        table.resetRowSelection();
      }
      if (onPageSizeChange) {
        onPageSizeChange(size);
      } else {
        table.setPageSize(size);
        table.setPageIndex(0);
      }
    },
    [table, onPageSizeChange, enableRowSelection],
  );

  const selectionAtCap = isBulkSelectionAtCap(rowSelectionState);

  const handleToggleAllPageRows = useCallback(() => {
    const pageRowIds = table
      .getRowModel()
      .rows.filter((row) => row.getCanSelect())
      .map((row) => row.id);
    const allPageSelected = table.getIsAllPageRowsSelected();
    const somePageSelected = table.getIsSomePageRowsSelected();
    table.setRowSelection((prev) =>
      applyPageSelectAll(
        prev,
        pageRowIds,
        allPageSelected || (isBulkSelectionAtCap(prev) && somePageSelected),
      ),
    );
  }, [table]);

  const handleRowClick = useCallback(
    (e: React.MouseEvent, rowData: TData) => {
      if (!onRowClick) return;
      const target = e.target as HTMLElement;
      if (
        target.closest(
          "button, a, input, select, textarea, [role=menu], [role=menuitem]",
        )
      )
        return;
      onRowClick(rowData);
    },
    [onRowClick],
  );

  const minTableWidth = useMemo(() => {
    const colWidth = columns.reduce((sum, col) => sum + col.getSize(), 0);
    return colWidth + (enableRowSelection ? 48 : 0);
  }, [columns, enableRowSelection]);

  const colSpan = columns.length + (enableRowSelection ? 1 : 0);

  const isCompact = density === "compact";
  const cellPy = isCompact ? "py-0.5" : "py-1.5";
  const cellText = isCompact ? "text-[13px]" : "";

  const stickyColCls = getStickyFirstColumnClassName(stickyFirstColumn);
  const stickyColShadow = "";
  const stickyHeadColCls =
    getStickyFirstColumnHeaderClassName(stickyFirstColumn);
  const hasScrollContainer = Boolean(scrollContainerClassName);

  return (
    <div
      className={cn(
        "flex min-w-0 flex-col",
        fillHeight && "min-h-0 flex-1",
        className,
      )}
    >
      {/* Bulk action bar */}
      {enableRowSelection && (
        <BulkActionBar
          selectedCount={selectedRows.length}
          onClear={() => table.resetRowSelection()}
          actions={bulkActions}
        />
      )}

      {/* Table */}
      <div
        className={cn(
          "min-w-0",
          fillHeight && "flex min-h-0 flex-1 flex-col",
          scrollContainerClassName,
        )}
      >
        <Table
          className={cn(cellText, "min-w-full")}
          style={{ minWidth: minTableWidth }}
          disableWrapperOverflow={hasScrollContainer}
          horizontalScrollOnly={horizontalScrollOnly}
          wrapperClassName={
            hasScrollContainer
              ? undefined
              : cn(
                  TABLE_SCROLL_SHRINK_CLASS,
                  fillHeight && TABLE_FILL_HEIGHT_CLASS,
                )
          }
        >
          <TableHeader
            className={cn(
              stickyHeader &&
                "sticky top-0 z-20 bg-white shadow-[0_1px_0_0_theme(colors.gray.200)]",
            )}
          >
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="hover:bg-transparent">
                {enableRowSelection && (
                  <TableHead
                    className={cn("w-12", stickyHeader && "bg-white")}
                    style={{ width: 48, minWidth: 48 }}
                  >
                    <TableCheckbox
                      checked={table.getIsAllPageRowsSelected()}
                      indeterminate={table.getIsSomePageRowsSelected()}
                      onChange={handleToggleAllPageRows}
                      ariaLabel="Select all rows"
                    />
                  </TableHead>
                )}
                {headerGroup.headers.map((header, idx) => {
                  const col = header.column;
                  const canSort = col.getCanSort();
                  const isFirstDataCol = idx === 0;
                  const headerMeta = col.columnDef.meta as
                    ColumnMetaConfig | undefined;

                  return (
                    <TableHead
                      key={header.id}
                      className={cn(
                        isFirstDataCol && stickyHeadColCls,
                        isFirstDataCol && stickyColShadow,
                      )}
                      style={getColumnSizeStyle(header.getSize(), headerMeta)}
                    >
                      {header.isPlaceholder ? null : canSort ? (
                        <SortableHeader
                          column={col}
                          label={
                            typeof col.columnDef.header === "string"
                              ? col.columnDef.header
                              : (flexRender(
                                  col.columnDef.header,
                                  header.getContext(),
                                ) as string)
                          }
                        />
                      ) : (
                        flexRender(col.columnDef.header, header.getContext())
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody>
            {showSkeleton ? (
              Array.from({ length: 8 }).map((_, i) => (
                <TableRow key={`skel-${i}`} className="hover:bg-transparent">
                  {enableRowSelection && (
                    <TableCell className={cn(cellPy, "w-12")}>
                      <div className="h-4 w-4 animate-pulse rounded bg-muted" />
                    </TableCell>
                  )}
                  {columns.map((col) => (
                    <TableCell key={`${col.id}-skel-${i}`} className={cellPy}>
                      <div className="h-4 w-full animate-pulse rounded bg-muted" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : error ? (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={colSpan} className="py-12 text-center">
                  <div className="text-status-error-text font-medium mb-1">
                    Error loading data
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {error.message || "An unexpected error occurred"}
                  </div>
                </TableCell>
              </TableRow>
            ) : showEmpty ? (
              <EmptyState
                inTable
                colSpan={colSpan}
                variant={emptyVariant ?? "general"}
                title={emptyTitle}
                description={emptyMessage}
                action={emptyAction}
              />
            ) : (
              rows.map((row) => {
                const canSelect = row.getCanSelect();
                const isSelected = enableRowSelection && row.getIsSelected();

                return (
                  <TableRow
                    key={row.id}
                    data-state={isSelected ? "selected" : undefined}
                    className={cn(
                      "group",
                      onRowClick && "cursor-pointer",
                      rowClassName?.(row.original),
                    )}
                    onClick={(e) => handleRowClick(e, row.original)}
                  >
                    {enableRowSelection && (
                      <TableCell
                        className={cn(
                          cellPy,
                          "w-12",
                          !canSelect || (selectionAtCap && !isSelected)
                            ? "cursor-not-allowed"
                            : "cursor-pointer",
                        )}
                        style={{ width: 48, minWidth: 48 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!canSelect) return;
                          if (selectionAtCap && !isSelected) return;
                          row.toggleSelected();
                        }}
                      >
                        <TableCheckbox
                          checked={isSelected}
                          disabled={
                            !canSelect || (selectionAtCap && !isSelected)
                          }
                          onChange={() => {
                            if (!canSelect) return;
                            if (selectionAtCap && !isSelected) return;
                            row.toggleSelected();
                          }}
                          ariaLabel={`Select row ${row.id}`}
                        />
                      </TableCell>
                    )}
                    {row.getVisibleCells().map((cell, idx) => {
                      const cellMeta = cell.column.columnDef.meta as
                        ColumnMetaConfig | undefined;
                      const isFirstDataCol = idx === 0;

                      return (
                        <TableCell
                          key={cell.id}
                          className={cn(
                            cellPy,
                            cellContentNeedsMinWidthZero(cellMeta) &&
                              "overflow-hidden",
                            isFirstDataCol && stickyColCls,
                            isFirstDataCol && stickyColShadow,
                          )}
                          style={getColumnSizeStyle(
                            cell.column.getSize(),
                            cellMeta,
                          )}
                        >
                          <div
                            className={cn(
                              cellContentNeedsMinWidthZero(cellMeta) &&
                                "min-w-0",
                              getCellTextClass(cellMeta),
                            )}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </div>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {!hidePagination && !isLoading && !error && totalCount > 0 && (
        <div className="shrink-0 px-4 py-3">
          <ModernPagination
            currentPage={paginationState.pageIndex + 1}
            totalPages={pageCount}
            totalItems={totalCount}
            itemsPerPage={paginationState.pageSize}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handlePageSizeChange}
            showPageSizeSelector
            pageSizeOptions={pageSizeOptions}
            loading={isLoading}
          />
        </div>
      )}
    </div>
  );
}

// =============================================================================
// DATA TABLE TOOLBAR (bridge for config-based toolbar)
// =============================================================================

export interface SearchConfig {
  enabled: boolean;
  placeholder?: string;
  fields?: Array<{ id: string; label: string }>;
}

export interface FilterConfig {
  enabled: boolean;
  filters: InlineFilterConfig[];
}

export interface DataTableToolbarProps {
  searchConfig?: SearchConfig;
  filterConfig?: FilterConfig;

  searchValue?: string;
  onSearchChange?: (value: string) => void;
  filterValues?: FilterValues;
  onFiltersChange?: (values: FilterValues) => void;

  showFilterSelector?: boolean;
  recentSearches?: string[];
  onClearRecentSearches?: () => void;

  /** TanStack table instance — required when showColumnToggle is true */
  table?: {
    getAllLeafColumns: () => ReadonlyArray<{
      id: string;
      getCanHide: () => boolean;
      getIsVisible: () => boolean;
      getCanSort?: () => boolean;
      getIsSorted?: () => false | "asc" | "desc";
      toggleVisibility: (value: boolean) => void;
      columnDef: { header?: unknown };
    }>;
    getColumn: (columnId: string) =>
      | (ToolbarSortColumn & {
          toggleVisibility: (value: boolean) => void;
          getIsVisible: () => boolean;
          getCanHide: () => boolean;
        })
      | undefined;
    setColumnOrder?: (_order: string[]) => void;
    options?: {
      onColumnOrderChange?: unknown;
      state?: { columnOrder?: string[] };
    };
    state: {
      columnVisibility?: Record<string, boolean>;
      columnOrder?: string[];
      sorting?: Array<{ id: string; desc: boolean }>;
    };
  };
  /** Render column visibility toggle in the toolbar */
  showColumnToggle?: boolean;
  /**
   * Enable drag-reorder of unfixed columns. Only takes effect when the table
   * instance has a controlled `columnOrder` (`onColumnOrderChange` +
   * `options.state.columnOrder`), typically `enableColumnOrdering: true`.
   */
  enableColumnReordering?: boolean;

  /** Inline page title rendered at the start of the toolbar row */
  title?: string;
  /** Action element rendered on the far right (e.g. "New" button) */
  titleAction?: React.ReactNode;

  children?: React.ReactNode;
  /** Always-visible control rendered immediately after search on the left */
  leadingContent?: React.ReactNode;
  /**
   * Client-scoped table key. When set, funnel on/off chips persist as a
   * local view (`{storageKey}-filter-visibility`).
   */
  storageKey?: string;
  /** One-time upgrade of a legacy funnel blob. See `useFilterVisibility`. */
  filterVisibilityPersistence?: FilterVisibilityPersistence;
}

const DEFAULT_FILTER_VALUES: FilterValues = {};

export function DataTableToolbar({
  searchConfig,
  filterConfig,
  searchValue = "",
  onSearchChange,
  filterValues = DEFAULT_FILTER_VALUES,
  onFiltersChange,
  showFilterSelector = true,
  recentSearches,
  onClearRecentSearches,
  table,
  showColumnToggle = false,
  enableColumnReordering = true,
  title,
  titleAction,
  children,
  leadingContent,
  storageKey,
  filterVisibilityPersistence,
}: Readonly<DataTableToolbarProps>) {
  const singleSelectFilterIds = useMemo(
    () =>
      (filterConfig?.filters ?? [])
        .filter((filter) => filter.type === "select" && !filter.multiple)
        .map((filter) => filter.id),
    [filterConfig?.filters],
  );

  const normalizedFilterValues = useMemo(
    () => coerceSingleSelectFilters(filterValues, singleSelectFilterIds),
    [filterValues, singleSelectFilterIds],
  );

  // Write scalars back so persisted arrays cannot keep applying `in:` while
  // the chip shows only the first value.
  useEffect(() => {
    if (normalizedFilterValues === filterValues) return;
    onFiltersChange?.(normalizedFilterValues);
  }, [filterValues, normalizedFilterValues, onFiltersChange]);

  const {
    enabledFilterIds,
    handleToggleFilterVisibility,
    handleSetAllFilterVisibility,
  } = useFilterVisibility(
    filterConfig?.filters,
    normalizedFilterValues,
    onFiltersChange,
    storageKey,
    filterVisibilityPersistence,
  );

  const handleFilterChange = useCallback(
    (newFilters: FilterValues) => {
      onFiltersChange?.(
        coerceSingleSelectFilters(newFilters, singleSelectFilterIds),
      );
    },
    [onFiltersChange, singleSelectFilterIds],
  );

  const availableFilters = useMemo<AvailableFilter[]>(() => {
    if (!filterConfig?.filters) return [];

    return filterConfig.filters.map((filter) => {
      const sortColumnId = filter.sortColumnId ?? filter.id;
      // Look up by id without table.getColumn() — that logs when the
      // filter id is an API field (location_id, agg_type) rather than a
      // column id.
      const sortColumn = table
        ?.getAllLeafColumns()
        ?.find((column) => column.id === sortColumnId);
      const sortControls = filterSortControlsForColumn(sortColumn);

      if (filter.type === "select" && filter.options) {
        const opts: SearchableSelectOption[] = filter.options.map((o) => ({
          value: o.value,
          label: o.label,
          ...(o.group ? { group: o.group } : {}),
        }));

        if (filter.multiple) {
          const raw = normalizedFilterValues[filter.id];
          const vals: string[] = Array.isArray(raw)
            ? raw
            : typeof raw === "string" && raw && raw !== "all"
              ? [raw]
              : [];

          if (filter.specificationOutcomeToolbar) {
            return {
              id: filter.id,
              label: filter.label,
              component: (
                <SpecificationFilterSelect
                  key={filter.id}
                  value={vals}
                  onChange={(v: string[]) =>
                    handleFilterChange({ ...filterValues, [filter.id]: v })
                  }
                  options={opts}
                  placeholder={filter.label}
                  searchPlaceholder={`Search ${filter.label.toLowerCase()}…`}
                  disabled={filter.disabled}
                  aria-label={filter.label}
                  dropdownHeader={sortControls}
                />
              ),
            };
          }

          if (filter.procedureOutcomeToolbar) {
            return {
              id: filter.id,
              label: filter.label,
              component: (
                <ProcedureFilterSelect
                  key={filter.id}
                  value={vals}
                  onChange={(v: string[]) =>
                    handleFilterChange({ ...filterValues, [filter.id]: v })
                  }
                  options={opts}
                  placeholder={filter.label}
                  searchPlaceholder={`Search ${filter.label.toLowerCase()}…`}
                  disabled={filter.disabled}
                  aria-label={filter.label}
                  dropdownHeader={sortControls}
                />
              ),
            };
          }

          return {
            id: filter.id,
            label: filter.label,
            component: (
              <SearchableSelect
                key={filter.id}
                value={vals}
                onChange={(v: string[]) =>
                  handleFilterChange({
                    ...normalizedFilterValues,
                    [filter.id]: v,
                  })
                }
                options={opts}
                placeholder={filter.label}
                searchPlaceholder={`Search ${filter.label.toLowerCase()}…`}
                multiple
                fitToContent={filter.fitToContent}
                minDropdownWidth={filter.minDropdownWidth}
                maxDropdownWidth={filter.maxDropdownWidth}
                searchable={filter.searchable}
                disabled={filter.disabled}
                aria-label={filter.label}
                showFieldPrefix
                dropdownHeader={sortControls}
              />
            ),
          };
        }

        const currentValue =
          (normalizedFilterValues[filter.id] as string) || "all";

        return {
          id: filter.id,
          label: filter.label,
          component: (
            <div title={filter.disabled ? filter.disabledReason : undefined}>
              <SearchableSelect
                key={filter.id}
                value={currentValue === "all" ? "" : currentValue}
                onChange={(v: string) =>
                  handleFilterChange({
                    ...normalizedFilterValues,
                    [filter.id]: v || "all",
                  })
                }
                options={opts}
                placeholder={filter.label}
                searchPlaceholder={`Search ${filter.label.toLowerCase()}…`}
                fitToContent={filter.fitToContent}
                minDropdownWidth={filter.minDropdownWidth}
                maxDropdownWidth={filter.maxDropdownWidth}
                searchable={filter.searchable}
                disabled={filter.disabled}
                aria-label={filter.label}
                showFieldPrefix
                dropdownHeader={sortControls}
              />
            </div>
          ),
        };
      }

      if (filter.type === "daterange") {
        const dateVal = normalizeDateRangeValue(
          normalizedFilterValues[filter.id],
        );

        return {
          id: filter.id,
          label: filter.label,
          component: (
            <DateRangeDropdown
              key={filter.id}
              value={dateVal}
              onChange={(v: DateRangeValue) =>
                handleFilterChange({
                  ...normalizedFilterValues,
                  [filter.id]: v,
                })
              }
              placeholder={filter.label}
              aria-label={filter.label}
              showFieldPrefix
              dropdownHeader={sortControls}
            />
          ),
        };
      }

      return {
        id: filter.id,
        label: filter.label,
        component: (
          <InlineFilters
            key={filter.id}
            filters={[filter]}
            values={normalizedFilterValues}
            onChange={handleFilterChange}
          />
        ),
      };
    });
    // table instance is stable; sorting must retrigger sort-control UI
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filterConfig,
    normalizedFilterValues,
    handleFilterChange,
    table,
    table?.state.sorting,
  ]);

  // Build column selector data from table instance
  const columnSelectorColumns = useMemo<TableColumn[] | undefined>(() => {
    if (!table || !showColumnToggle) return undefined;
    const allCols = table.getAllLeafColumns();
    const firstLeafId = allCols[0]?.id;
    return allCols
      .filter((col) => col.id !== "actions")
      .map((col) => toSelectorColumn(col, firstLeafId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    table,
    showColumnToggle,
    table?.state.columnVisibility,
    table?.state.columnOrder,
    table?.state.sorting,
  ]);

  const handleColumnVisibilityChange = useCallback(
    (columnId: string, visible: boolean) => {
      if (!table) return;
      const firstLeafId = table.getAllLeafColumns()[0]?.id;
      const column = table.getColumn(columnId);
      const isLocked =
        columnId === firstLeafId || (column ? !column.getCanHide() : false);
      if (isLocked && !visible) return;
      if (column) {
        column.toggleVisibility(visible);
      }
    },
    [table],
  );

  const columnReorderingEnabled = Boolean(
    enableColumnReordering &&
    typeof table?.options?.onColumnOrderChange === "function" &&
    isColumnOrderStateControlled(table.options.state),
  );

  const handleColumnOrderChange = useCallback(
    (movableIds: string[]) => {
      if (!table?.setColumnOrder) return;
      const fullOrder = table.getAllLeafColumns().map((col) => col.id);
      table.setColumnOrder(applyMovableOrder(fullOrder, movableIds));
    },
    [table],
  );

  const handleColumnSortChange = useCallback(
    (columnId: string, direction: false | "asc" | "desc") => {
      applyColumnSortDirection(table?.getColumn(columnId), direction);
    },
    [table],
  );

  const handleResetColumnOrder = useCallback(() => {
    table?.setColumnOrder?.([]);
  }, [table]);

  const showToolbar =
    searchConfig?.enabled ||
    filterConfig?.enabled ||
    children ||
    leadingContent ||
    showColumnToggle ||
    title;

  if (!showToolbar) return null;

  return (
    <EnhancedTableToolbar
      searchValue={searchValue}
      onSearchChange={onSearchChange}
      searchPlaceholder={searchConfig?.placeholder}
      searchFields={searchConfig?.fields}
      recentSearches={recentSearches}
      onClearRecentSearches={onClearRecentSearches}
      availableFilters={availableFilters}
      enabledFilters={enabledFilterIds}
      appliedFilterIds={appliedFilterIdsOf(
        filterConfig?.filters,
        normalizedFilterValues,
      )}
      onToggleFilterVisibility={handleToggleFilterVisibility}
      onSetAllFilterVisibility={handleSetAllFilterVisibility}
      showSearch={searchConfig?.enabled}
      showAdvancedFilters={
        showFilterSelector !== false &&
        filterConfig?.enabled === true &&
        (filterConfig.filters?.length ?? 0) > 0
      }
      showColumnSelector={showColumnToggle && !!columnSelectorColumns}
      columnSelectorColumns={columnSelectorColumns}
      onColumnVisibilityChange={
        showColumnToggle ? handleColumnVisibilityChange : undefined
      }
      enableColumnReordering={columnReorderingEnabled}
      onColumnOrderChange={
        columnReorderingEnabled ? handleColumnOrderChange : undefined
      }
      onResetColumnOrder={
        columnReorderingEnabled ? handleResetColumnOrder : undefined
      }
      onColumnSortChange={showColumnToggle ? handleColumnSortChange : undefined}
      title={title}
      titleAction={titleAction}
      leadingContent={leadingContent}
    >
      {children}
    </EnhancedTableToolbar>
  );
}
